// src/utils/liveClaim.ts
// ─────────────────────────────────────────────────────────────────
// Phase 3: Claim evidence auto-pull and claim assembly
//
// When a PM clicks "Generate claim", this module:
//   1. Pulls real WI step completion records for the milestone's
//      linked WIs from wiStepCompletion.ts
//   2. Pulls real GRN receipt records from canonicalBOM.ts
//   3. Pulls real ITP hold point clearances
//   4. Assembles a draft evidence pack the PM can review,
//      include/exclude individual items, and then lock
//
// The PM has full override — any item can be toggled in/out.
// Once the PM accepts, the claim moves to READY_TO_SUBMIT.
// ─────────────────────────────────────────────────────────────────

import { getAllStepCompletions, getAllHoldPointClearances, type StepCompletionRecord, type HoldPointClearance } from './wiStepCompletion'
import { getBOMForJob, type CanonicalBOMLine } from './canonicalBOM'
import { MOCK_JOB_PACKAGES } from './mockDataWorkInstructions'

export type EvidenceItemType =
  | 'WI_STEP'        // WI step completed by field crew (with photos)
  | 'HOLD_POINT'     // ITP hold point cleared by PM/PC + witness
  | 'GRN_RECEIPT'    // Materials received and GRN recorded
  | 'WI_COMPLETE'    // Whole work instruction marked complete

export interface LiveEvidenceItem {
  id: string
  type: EvidenceItemType
  // Display fields
  reference: string       // WI ref, HP ref, PO number, etc.
  title: string
  detail: string          // who, when, what was done
  completedByName: string
  completedAt: string     // ISO
  photoCount: number
  gpsAddress?: string | null
  witnessName?: string | null
  // Claim inclusion
  included: boolean       // PM can toggle; all true by default
  // Source IDs for traceability
  sourceId: string        // StepCompletion.id, holdPointRef, etc.
}

export interface ClaimEvidencePack {
  milestoneId: string
  jobId: string
  pulledAt: string
  pulledByName: string
  items: LiveEvidenceItem[]
  totalItems: number
  includedItems: number
  hasWIEvidence: boolean
  hasHoldPointEvidence: boolean
  hasMaterialEvidence: boolean
  locked: boolean   // PM has accepted the pack; no further changes
}

const PACK_KEY_PREFIX = 'tp_claim_evidence_pack_'

function canStore() { return typeof window !== 'undefined' }

export function getClaimEvidencePack(milestoneId: string): ClaimEvidencePack | null {
  if (!canStore()) return null
  try {
    const raw = window.localStorage.getItem(PACK_KEY_PREFIX + milestoneId)
    return raw ? JSON.parse(raw) as ClaimEvidencePack : null
  } catch { return null }
}

function savePack(pack: ClaimEvidencePack) {
  if (!canStore()) return
  window.localStorage.setItem(PACK_KEY_PREFIX + pack.milestoneId, JSON.stringify(pack))
}

function packStats(items: LiveEvidenceItem[]) {
  return {
    totalItems: items.length,
    includedItems: items.filter(i => i.included).length,
    hasWIEvidence: items.some(i => i.type === 'WI_STEP' || i.type === 'WI_COMPLETE'),
    hasHoldPointEvidence: items.some(i => i.type === 'HOLD_POINT'),
    hasMaterialEvidence: items.some(i => i.type === 'GRN_RECEIPT'),
  }
}

/**
 * Pull all real completion evidence for a milestone and store it.
 * Called when PM clicks "Pull from field records" on the EW tab.
 */
export function pullAndAssembleEvidence(
  milestoneId: string,
  jobId: string,
  linkedWIs: string[],
  wiIdMap: Record<string, string>,
  pulledByName: string,
): ClaimEvidencePack {
  const items: LiveEvidenceItem[] = []
  const allCompletions = getAllStepCompletions()
  const allHoldPoints  = getAllHoldPointClearances()
  const bomLines       = getBOMForJob(jobId)
  const now = new Date().toISOString()

  // ── 1. WI step completions ────────────────────────────────────
  linkedWIs.forEach(wiRef => {
    const wiId = wiIdMap[wiRef] ?? wiRef
    const wi   = MOCK_JOB_PACKAGES.find(w => w.id === wiId || w.reference === wiRef)
    const stepCompletions = allCompletions.filter(c => c.wiId === wiId && !c.isHoldPoint)

    stepCompletions.forEach(c => {
      items.push({
        id: `ew_step_${c.id}`,
        type: 'WI_STEP',
        reference: `${wi?.reference ?? wiRef} · Step ${c.stepNumber}`,
        title: c.stepTitle,
        detail: `${c.photoCount} photo${c.photoCount !== 1 ? 's' : ''} · verified by ${c.verifiedByName ?? 'PM/PC'}`,
        completedByName: c.completedByName,
        completedAt: c.completedAt,
        photoCount: c.photoCount,
        gpsAddress: c.gpsAddress ?? null,
        witnessName: c.verifiedByName ?? null,
        included: true,
        sourceId: c.id,
      })
    })

    // If WI is marked complete in the static data (legacy status)
    if (wi && wi.status === 'COMPLETE' && wi.completedByName && wi.completedAt) {
      items.push({
        id: `ew_wi_${wi.id}`,
        type: 'WI_COMPLETE',
        reference: wi.reference,
        title: `Work instruction complete — ${wi.title}`,
        detail: `All ${wi.steps.length} steps signed off`,
        completedByName: wi.completedByName,
        completedAt: wi.completedAt,
        photoCount: wi.steps.reduce((s, st) => s + (st.photoCount ?? 0), 0),
        included: true,
        sourceId: wi.id,
      })
    }
  })

  // ── 2. Hold point clearances ──────────────────────────────────
  allHoldPoints
    .filter(h => linkedWIs.includes(h.wiReference) || (wiIdMap && Object.values(wiIdMap).includes(h.wiId)))
    .forEach(h => {
      if (items.find(i => i.sourceId === h.holdPointRef)) return // already included via step record
      items.push({
        id: `ew_hp_${h.holdPointRef}`,
        type: 'HOLD_POINT',
        reference: h.holdPointRef,
        title: `Hold point cleared — ${h.holdPointRef}`,
        detail: `Witnessed by ${h.witnessName ?? 'authorised person'} (${h.witnessOrg ?? ''})${h.notes ? ' · ' + h.notes : ''}`,
        completedByName: h.clearedByName,
        completedAt: h.clearedAt,
        photoCount: 0,
        witnessName: h.witnessName ?? null,
        included: true,
        sourceId: h.holdPointRef,
      })
    })

  // ── 3. Materials received (GRN records) ───────────────────────
  bomLines
    .filter(l => l.materialType === 'BUILD_MATERIAL' && (l.grnIds?.length ?? 0) > 0 && l.receivedQty && l.receivedQty > 0)
    .forEach(l => {
      items.push({
        id: `ew_grn_${l.id}`,
        type: 'GRN_RECEIPT',
        reference: l.poNumber ?? l.code,
        title: `Materials received — ${l.description}`,
        detail: `${l.receivedQty} ${l.unit} received · ${l.complianceFlag === 'COMPLIANT' ? 'Compliant ✓' : 'Compliance: ' + l.complianceFlag}`,
        completedByName: 'Warehouse / Site crew',
        completedAt: l.updatedAt,
        photoCount: 0,
        gpsAddress: null,
        included: l.complianceVerifiedForThisJob, // only include compliant materials by default
        sourceId: l.id,
      })
    })

  // Sort: hold points first, then WI steps by date, then GRN
  const order: Record<EvidenceItemType, number> = { HOLD_POINT: 0, WI_COMPLETE: 1, WI_STEP: 2, GRN_RECEIPT: 3 }
  items.sort((a, b) => (order[a.type] - order[b.type]) || a.completedAt.localeCompare(b.completedAt))

  const pack: ClaimEvidencePack = {
    milestoneId, jobId, pulledAt: now, pulledByName, items,
    ...packStats(items),
    locked: false,
  }
  savePack(pack)
  return pack
}

export function toggleEvidenceItem(milestoneId: string, itemId: string): ClaimEvidencePack | null {
  const pack = getClaimEvidencePack(milestoneId)
  if (!pack || pack.locked) return pack
  const updated = { ...pack, items: pack.items.map(i => i.id === itemId ? { ...i, included: !i.included } : i) }
  const stats = packStats(updated.items)
  const final = { ...updated, ...stats }
  savePack(final)
  return final
}

export function lockEvidencePack(milestoneId: string): ClaimEvidencePack | null {
  const pack = getClaimEvidencePack(milestoneId)
  if (!pack) return null
  const final = { ...pack, locked: true }
  savePack(final)
  return final
}

export function clearEvidencePack(milestoneId: string) {
  if (canStore()) window.localStorage.removeItem(PACK_KEY_PREFIX + milestoneId)
}

export const EVIDENCE_TYPE_META: Record<EvidenceItemType, { label: string; icon: string; bg: string; text: string }> = {
  WI_STEP:    { label: 'WI step',       icon: '📋', bg: 'bg-purple-50',  text: 'text-purple-700' },
  WI_COMPLETE:{ label: 'WI complete',   icon: '✅', bg: 'bg-green-50',   text: 'text-green-700' },
  HOLD_POINT: { label: 'Hold point',    icon: '🔒', bg: 'bg-blue-50',    text: 'text-blue-700' },
  GRN_RECEIPT:{ label: 'Material GRN',  icon: '📦', bg: 'bg-teal-50',   text: 'text-teal-700' },
}
